import React, { useState, useEffect } from "react";
import styled from "styled-components";
import API from "../../../util/api";
import ConfirmationModal from "../../Common/ConfirmationModal";
import { actionContainerStyles, actionLinkStyles, actionHeaderStyles } from "../common/ActionStyles";
import ResponsiveActions from "../common/ResponsiveActions";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const LINK_HOVER_COLOR = "#F7941D";

const Container = styled.div`
  padding: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: stretch;
`;

const Actions = styled.div`
  ${actionContainerStyles};

  h3 {
    ${actionHeaderStyles};
    border-bottom: 1px solid ${({ colors }) => colors.border};
  }
  ul {
    list-style: none;
    margin: 0;
    padding: 12px 16px;
  }
  li + li {
    margin-top: 4px;
  }
  a {
    ${actionLinkStyles};
    padding-left: 16px;
    color: #2C4960;
    &:hover {
      color: ${LINK_HOVER_COLOR};
    }
  }

  @media (min-width: 768px) {
    flex: 0 0 260px;
  }
`;

const View = styled.div`
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Table = styled.table`
  ${adminTable};
  font-size: 14px;

  thead th {
    ${adminTableHeaderCell};
  }

  tbody td {
    ${adminTableDataCell};
  }
`;

const LinkButton = styled.a`
  margin-right: 8px;
  color: white;
  background: ${({ colors }) => colors.buttonBackground};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    background: ${({ colors }) => colors.buttonHoverBackground};
  }
`;

const Form = styled.form`
  background: ${({ colors }) => colors.cardBackground};
  padding: 20px;
  border-radius: 5px;
`;

const Field = styled.div`
  margin-bottom: 1em;
  label {
    display: block;
    margin-bottom: 0.3em;
    color: ${({ colors }) => colors.labelText};
    font-size: 14px;
  }
  input[type="text"] {
    width: 100%;
    padding: 8px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 4px;
    font-size: 14px;
  }
`;

const SubmitRow = styled.div`
  margin-top: 1.5em;
  input[type="submit"] {
    padding: 8px 16px;
    background: ${({ colors }) => colors.buttonBackground};
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    &:hover {
      background: ${({ colors }) => colors.buttonHoverBackground};
    }
  }
`;

export default function ProductCodesContent({ colors }) {
  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState([]);

  // view state: 'list' | 'add' | 'edit'
  const [view, setView] = useState("list");

  // edit/add state
  const [selected, setSelected] = useState(null);
  const [originalCode, setOriginalCode] = useState("");
  const [originalDesc, setOriginalDesc] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [descInput, setDescInput] = useState("");

  // delete confirmation modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteCode, setPendingDeleteCode] = useState(null);

  // result modal state
  const [resultOpen, setResultOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [modalStatus, setModalStatus] = useState("Success");

  // load all product codes
  const loadCodes = async () => {
    setLoading(true);
    try {
      const resp = await API.getProductCodes({});
      setCodes(resp.result || []);
    } catch (err) {
      console.error("Failed to load product codes:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCodes();
  }, []);

  // open delete confirmation
  const onDeleteClick = (code) => {
    setPendingDeleteCode(code);
    setConfirmOpen(true);
  };

  // handle confirmed delete
  const handleDeleteConfirm = async () => {
    setConfirmOpen(false);
    setModalStatus("Success");
    try {
      const resp = await API.deleteProductCode({ product_code: pendingDeleteCode });
      const status = resp?.status === "Failed" ? "Failed" : "Success";
      setModalTitle(status === "Failed" ? "Error" : "Deleted");
      setModalMessage(
        resp?.message ||
        (status === "Failed"
          ? "Failed to delete that product code."
          : `Product code "${pendingDeleteCode}" deleted.`)
      );
      setModalStatus(status);
      await loadCodes();
    } catch (err) {
      console.error("Delete failed:", err);
      setModalTitle("Error");
      setModalMessage("Failed to delete that product code.");
      setModalStatus("Failed");
    } finally {
      setResultOpen(true);
      setPendingDeleteCode(null);
    }
  };

  // open edit form
  const openEdit = (pc) => {
    setSelected(pc);
    setOriginalCode(pc.product_code);
    setOriginalDesc(pc.product_desc);
    setCodeInput(pc.product_code);
    setDescInput(pc.product_desc);
    setView("edit");
  };

  // submit edits
  const handleUpdate = async (e) => {
    e.preventDefault();
    setModalStatus("Success");
    try {
      const resp = await API.updateProductCodes({
        old_code: originalCode,
        old_desc: originalDesc,
        product_code: codeInput,
        product_desc: descInput,
      });
      const status = resp?.status === "Failed" ? "Failed" : "Success";
      setModalTitle(status === "Failed" ? "Error" : "Success");
      setModalMessage(
        resp?.message ||
        (status === "Failed" ? "Update failed." : "Product code updated.")
      );
      setModalStatus(status);
      await loadCodes();
      setView("list");
    } catch (err) {
      console.error(err);
      setModalTitle("Error");
      setModalMessage("Update failed.");
      setModalStatus("Failed");
    } finally {
      setResultOpen(true);
    }
  };

  // submit new code
  const handleAdd = async (e) => {
    e.preventDefault();
    setModalStatus("Success");
    try {
      const resp = await API.addProductCodes({
        product_code: codeInput,
        product_desc: descInput,
        product_rank: codes.length + 1,
      });
      const status = resp?.status === "Failed" ? "Failed" : "Success";
      setModalTitle(status === "Failed" ? "Error" : "Success");
      setModalMessage(
        resp?.message ||
        (status === "Failed" ? "Add failed." : "Product code added.")
      );
      setModalStatus(status);
      await loadCodes();
      setView("list");
    } catch (err) {
      console.error(err);
      setModalTitle("Error");
      setModalMessage("Add failed.");
      setModalStatus("Failed");
    } finally {
      setResultOpen(true);
    }
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setModalStatus("Success");
  };

  if (loading) return <p>Loading…</p>;

  return (
    <Container>
      {/* Delete confirmation */}
      <ConfirmationModal
        show={confirmOpen}
        title="Confirm delete"
        message={`Delete product code "${pendingDeleteCode}"?`}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      {/* Result modal */}
      <ConfirmationModal
        show={resultOpen}
        title={modalTitle}
        message={modalMessage}
        onCancel={handleResultClose}
        onConfirm={handleResultClose}
        status={modalStatus}
      />

      {/* Actions pane */}
      <ResponsiveActions
        colors={colors}
        renderActions={() => (
          <Actions colors={colors}>
            <h3>Actions</h3>
            <ul>
              {view === "list" && (
                <li>
                  <a
                    onClick={() => {
                      setView("add");
                      setCodeInput("");
                      setDescInput("");
                    }}
                  >
                    Add
                  </a>
                </li>
              )}
              {(view === "add" || view === "edit") && (
                <li>
                  <a onClick={() => setView("list")}>Cancel</a>
                </li>
              )}
            </ul>
          </Actions>
        )}
      />

      <View>
        {view === "list" && (
          <Table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c.product_code}>
                  <td>{c.product_code}</td>
                  <td>{c.product_desc}</td>
                  <td>
                    <small>
                      <LinkButton
                        colors={colors}
                        onClick={() => openEdit(c)}
                      >
                        Edit
                      </LinkButton>
                      <LinkButton
                        colors={colors}
                        onClick={() => onDeleteClick(c.product_code)}
                      >
                        Delete
                      </LinkButton>
                    </small>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {view === "edit" && (
          <>
            <h1 style={{ color: colors.labelText }}>Edit Product Code</h1>
            <Form colors={colors} onSubmit={handleUpdate}>
              <Field colors={colors}>
                <label>Product Code</label>
                <input
                  type="text"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                />
              </Field>
              <Field colors={colors}>
                <label>Description</label>
                <input
                  type="text"
                  value={descInput}
                  onChange={(e) => setDescInput(e.target.value)}
                />
              </Field>
              <SubmitRow colors={colors}>
                <input type="submit" value="Submit" />
              </SubmitRow>
            </Form>
          </>
        )}

        {view === "add" && (
          <>
            <h1 style={{ color: colors.labelText }}>Add New Product Code</h1>
            <Form colors={colors} onSubmit={handleAdd}>
              <Field colors={colors}>
                <label>Product Code</label>
                <input
                  type="text"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                />
              </Field>
              <Field colors={colors}>
                <label>Description</label>
                <input
                  type="text"
                  value={descInput}
                  onChange={(e) => setDescInput(e.target.value)}
                />
              </Field>
              <SubmitRow colors={colors}>
                <input type="submit" value="Submit" />
              </SubmitRow>
            </Form>
          </>
        )}
      </View>

      <div style={{ clear: "both" }} />
    </Container>
  );
}
